import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  MapPinIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  ClockIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  EyeIcon,
  HeartIcon,
  CalendarIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { adsApi, authApi, userApi, BusinessApprovalStatus } from '../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

interface BusinessHours {
  monday: { open: string; close: string; closed: boolean };
  tuesday: { open: string; close: string; closed: boolean };
  wednesday: { open: string; close: string; closed: boolean };
  thursday: { open: string; close: string; closed: boolean };
  friday: { open: string; close: string; closed: boolean };
  saturday: { open: string; close: string; closed: boolean };
  sunday: { open: string; close: string; closed: boolean };
}

const BusinessStorefrontPage: React.FC = () => {
  const { businessId, slug } = useParams<{ businessId: string; slug?: string }>();
  const { user: currentUser } = useAuth();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showGalleryModal, setShowGalleryModal] = useState(false);

  // Fetch business user data - prioritize slug-only API for security
  const { data: businessUser, isLoading: isLoadingUser } = useQuery({
    queryKey: ['business-user', slug, businessId],
    queryFn: async () => {
      // If we have a slug but no businessId, use slug-only API (secure)
      if (slug && !businessId) {
        const response = await userApi.getBusinessBySlug(slug);
        // Return the full response data which contains User and GalleryImages
        return response;
      }
      // If we have both slug and businessId, use slug+ID API (backward compatibility)
      else if (slug && businessId) {
        const response = await userApi.getBusinessBySlugAndId(slug, businessId);
        
        // If there's a redirect slug, we should redirect to the correct URL
        if (response.data.redirectSlug && response.data.redirectSlug !== slug) {
          // For now, just return the user data - frontend can handle redirect if needed
          return { data: response.data.user };
        }
        
        return { data: response.data.user };
      }
      // If we only have businessId (old format), use ID-only API (backward compatibility)
      else if (businessId && !slug) {
        const response = await userApi.getById(businessId);
        return { data: response.data.User }; // Extract user data from nested response
      }
      throw new Error('No business identifier provided');
    },
    enabled: !!(slug || businessId)
  });

  // Extract user data from the response structure for queries
  const extractedUserData = (businessUser?.data as any)?.User || (businessUser?.data as any)?.user || businessUser?.data;
  
  // Fetch business ads
  const { data: businessAds, isLoading: isLoadingAds } = useQuery({
    queryKey: ['business-ads', extractedUserData?.id],
    queryFn: () => adsApi.getUserAds(extractedUserData?.id!),
    enabled: !!extractedUserData?.id
  });

  // Parse business hours from user data
  const businessHours: BusinessHours = React.useMemo(() => {
    if (!extractedUserData?.businessHours) {
      return {
        monday: { open: '09:00', close: '18:00', closed: false },
        tuesday: { open: '09:00', close: '18:00', closed: false },
        wednesday: { open: '09:00', close: '18:00', closed: false },
        thursday: { open: '09:00', close: '18:00', closed: false },
        friday: { open: '09:00', close: '17:00', closed: false },
        saturday: { open: '10:00', close: '16:00', closed: false },
        sunday: { open: '', close: '', closed: true }
      };
    }

    try {
      // If it's a string, parse it as JSON
      if (typeof extractedUserData.businessHours === 'string') {
        return JSON.parse(extractedUserData.businessHours);
      }
      // If it's already an object, return it
      return extractedUserData.businessHours;
    } catch (error) {
      console.error('Error parsing business hours:', error);
      return {
        monday: { open: '09:00', close: '18:00', closed: false },
        tuesday: { open: '09:00', close: '18:00', closed: false },
        wednesday: { open: '09:00', close: '18:00', closed: false },
        thursday: { open: '09:00', close: '18:00', closed: false },
        friday: { open: '09:00', close: '17:00', closed: false },
        saturday: { open: '10:00', close: '16:00', closed: false },
        sunday: { open: '', close: '', closed: true }
      };
    }
  }, [extractedUserData?.businessHours]);

  // Parse business gallery from user data
  const businessGallery = React.useMemo(() => {
    // Use the new GalleryImages array from the API response
    const galleryImages = (businessUser?.data as any)?.GalleryImages || (businessUser?.data as any)?.galleryImages;
    if (galleryImages && Array.isArray(galleryImages)) {
      return galleryImages.map((img: any) => img.imageUrl);
    }
    
    // Fallback to old businessGallery field for backward compatibility
    if (!extractedUserData?.businessGallery) {
      return [];
    }

    try {
      // If it's a string, parse it as JSON
      if (typeof extractedUserData.businessGallery === 'string') {
        return JSON.parse(extractedUserData.businessGallery);
      }
      // If it's already an array, return it
      return extractedUserData.businessGallery;
    } catch (error) {
      console.error('Error parsing business gallery:', error);
      return [];
    }
  }, [(businessUser?.data as any)?.GalleryImages, (businessUser?.data as any)?.galleryImages, extractedUserData?.businessGallery]);

  const getActivityPeriod = (dateJoined: string) => {
    const days = Math.floor((new Date().getTime() - new Date(dateJoined).getTime()) / (1000 * 60 * 60 * 24));
    
    if (days >= 30) {
      const months = Math.floor(days / 30);
      return { value: months, label: months === 1 ? 'ماه فعال' : 'ماه فعال' };
    } else if (days >= 7) {
      const weeks = Math.floor(days / 7);
      return { value: weeks, label: weeks === 1 ? 'هفته فعال' : 'هفته فعال' };
    } else {
      return { value: days, label: days === 1 ? 'روز فعال' : 'روز فعال' };
    }
  };

  const getDayName = (day: string) => {
    const days = {
      monday: 'دوشنبه',
      tuesday: 'سه‌شنبه', 
      wednesday: 'چهارشنبه',
      thursday: 'پنج‌شنبه',
      friday: 'جمعه',
      saturday: 'شنبه',
      sunday: 'یکشنبه'
    };
    return days[day as keyof typeof days];
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    const [hour, minute] = time.split(':');
    return `${hour}:${minute}`;
  };

  const isCurrentlyOpen = () => {
    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[now.getDay()];
    const currentTime = now.toTimeString().slice(0, 5);
    
    const todayHours = businessHours[currentDay as keyof BusinessHours];
    if (todayHours.closed) return false;
    
    return currentTime >= todayHours.open && currentTime <= todayHours.close;
  };

  const handleToggleFavorite = async (adId: string) => {
    try {
      await adsApi.toggleFavorite(adId);
      toast.success('وضعیت علاقه‌مندی تغییر کرد');
    } catch (error) {
      toast.error('خطا در تغییر وضعیت علاقه‌مندی');
    }
  };

  if (isLoadingUser || isLoadingAds) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                          <p className="mt-4 text-gray-600">در حال بارگذاری فروشگاه...</p>
        </div>
      </div>
    );
  }

  // Handle both nested and direct response structures  
  const userData = ((businessUser?.data as any)?.User || (businessUser?.data as any)?.user || businessUser?.data || businessUser) as any;
  
  // Extract user data from the response structure
  const actualUserData = (businessUser?.data as any)?.User || businessUser?.data;
  
  if (!userData || userData.userType !== 'Business') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <BuildingOfficeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">کسب و کار یافت نشد</h2>
          <p className="text-gray-600">این صفحه وجود ندارد یا کسب و کار فعال نیست.</p>
          <Link to="/" className="mt-4 inline-block bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700">
            بازگشت به خانه
          </Link>
        </div>
      </div>
    );
  }

  // Check if business is pending approval
  const approvalStatus = userData.businessApprovalStatus as any;
  const isApproved = approvalStatus === BusinessApprovalStatus.Approved || approvalStatus === 1 || approvalStatus === 'Approved';
  if (!isApproved) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ClockIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                          <h2 className="text-xl font-semibold text-gray-900 mb-2">فروشگاه در انتظار تأیید</h2>
                <p className="text-gray-600">این فروشگاه هنوز توسط مدیریت تأیید نشده است.</p>
          <Link to="/" className="mt-4 inline-block bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700">
            بازگشت به خانه
          </Link>
        </div>
      </div>
    );
  }

  const business = userData;
  const ads = businessAds?.data || [];

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Business Header */}
      <div className="relative bg-gradient-to-r from-purple-600 via-purple-700 to-pink-600 shadow-sm border-b overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-32 -translate-x-32"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-reverse lg:space-x-12">
            {/* Business Logo */}
            <div className="flex-shrink-0 mb-8 lg:mb-0">
              <div className="relative">
                <div className="w-40 h-40 bg-white rounded-full flex items-center justify-center border-4 border-white shadow-2xl overflow-hidden">
                  {business.profileImageUrl ? (
                    <img 
                      src={business.profileImageUrl} 
                      alt={business.businessName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-gray-600 text-5xl font-semibold">
                      {business.businessName?.charAt(0).toUpperCase() || 'B'}
                    </span>
                  )}
                </div>
                {business.isBusinessVerified && (
                  <div className="absolute -top-2 -left-2 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                    تأیید شده ✓
                  </div>
                )}
              </div>
            </div>

            {/* Business Info */}
            <div className="flex-1">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="text-4xl font-semibold text-white mb-2 drop-shadow-lg">
                    {business.businessName || 'نام کسب و کار'}
                  </h1>
                  <p className="text-lg text-white/90 mb-1">کسب و کار</p>
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20 backdrop-blur-sm border border-white/30 ${
                    isCurrentlyOpen() 
                      ? 'text-green-100' 
                      : 'text-red-100'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ml-2 ${
                      isCurrentlyOpen() ? 'bg-green-400' : 'bg-red-400'
                    }`}></div>
                    {isCurrentlyOpen() ? 'باز' : 'بسته'}
                  </div>
                </div>
                
                <div className="flex items-center space-x-reverse space-x-4">
                  {business.phoneNumber && (
                    <a
                      href={`tel:${business.phoneNumber}`}
                      className="flex items-center px-4 py-2 mr-4 bg-white text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors duration-200 shadow-lg"
                    >
                      <PhoneIcon className="h-4 w-4 ml-2" />
                      {business.phoneNumber}
                    </a>
                  )}
                  {business.email && (
                    <a
                      href={`mailto:${business.email}`}
                      className="flex items-center px-4 py-2  bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm font-medium rounded-lg hover:bg-white/30 transition-colors duration-200"
                    >
                      <EnvelopeIcon className="h-4 w-4 ml-2" />
                      ارسال ایمیل
                    </a>
                  )}
                  {/* Edit Button - Only show for business owner */}
                  {currentUser?.id === business.id && (
                    <Link
                      to="/profile?tab=settings"
                      className="flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm font-medium rounded-lg hover:bg-white/30 transition-colors duration-200"
                    >
                      <svg className="h-4 w-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      ویرایش
                    </Link>
                  )}
                </div>
              </div>

              <p className="text-white/90 text-lg mb-8 leading-relaxed max-w-4xl drop-shadow">
                {business.businessDescription || 'توضیحی برای این کسب و کار ارائه نشده است.'}
              </p>

              {/* Business Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
                <div className="text-center">
                  <div className="text-3xl font-semibold text-white mb-1 drop-shadow-lg">
                    {business.dateJoined ? 
                      getActivityPeriod(business.dateJoined).value
                      : '0'
                    }
                  </div>
                  <div className="text-sm text-white/80">
                    {business.dateJoined ? 
                      getActivityPeriod(business.dateJoined).label
                      : 'روز فعال'
                    }
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-semibold text-white mb-1 drop-shadow-lg">{ads.length}</div>
                  <div className="text-sm text-white/80">آگهی‌های منتشر شده</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-semibold text-white mb-1 drop-shadow-lg">
                    {ads.filter((ad: any) => ad.status === 'Active').length}
                  </div>
                  <div className="text-sm text-white/80">آگهی‌های فعال</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-semibold text-white mb-1 drop-shadow-lg">
                    {ads.reduce((sum: number, ad: any) => sum + (ad.viewCount || 0), 0) > 0 ? 
                      (ads.reduce((sum: number, ad: any) => sum + (ad.viewCount || 0), 0) / Math.max(ads.length, 1)).toFixed(1) 
                      : '0'
                    }
                  </div>
                  <div className="text-sm text-white/80">میانگین بازدید</div>
                </div>
              </div>

              {/* Additional Contact Info */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-white/80">
                {(business.city || business.province) && (
                  <div className="flex items-center">
                    <MapPinIcon className="h-4 w-4 ml-2" />
                    {business.city}{business.city && business.province && ', '}{business.province}
                  </div>
                )}
                {business.businessWebsite && (
                  <div className="flex items-center">
                    <GlobeAltIcon className="h-4 w-4 ml-2" />
                    <a 
                      href={business.businessWebsite} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-white hover:text-white/80 hover:underline"
                    >
                      وب‌سایت
                    </a>
                  </div>
                )}
                {business.dateJoined && (
                  <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 ml-2" />
                    عضو از {new Date(business.dateJoined).toLocaleDateString('fa-IR')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Business Gallery */}
            {businessGallery.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <PhotoIcon className="h-5 w-5 text-gray-500 ml-2" />
                    گالری تصاویر
                  </h2>
                  <button
                    onClick={() => setShowGalleryModal(true)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 transition-colors duration-200 border"
                  >
                    مشاهده همه
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {businessGallery.slice(0, 4).map((image: string, index: number) => (
                    <div 
                      key={index}
                      className="aspect-square bg-gray-100 rounded-md overflow-hidden cursor-pointer hover:opacity-90 transition-opacity border"
                      onClick={() => {
                        setSelectedImageIndex(index);
                        setShowGalleryModal(true);
                      }}
                    >
                      <img 
                        src={image} 
                        alt={`Image ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* About Us Section */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <BuildingOfficeIcon className="h-5 w-5 text-gray-500 ml-2" />
                درباره ما
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed">
                  {business.businessDescription || 'اطلاعات بیشتری درباره این کسب و کار در دسترس نیست.'}
                </p>
                {business.dateJoined && (
                  <div className="mt-4 flex items-center text-sm text-gray-500 bg-gray-50 p-3 rounded-md">
                    <CalendarIcon className="h-4 w-4 ml-2" />
                    عضو از {new Date(business.dateJoined).toLocaleDateString('fa-IR')}
                  </div>
                )}
              </div>
            </div>

            {/* Business Ads */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-semibold text-gray-900">
                  آگهی‌ها
                </h2>
              </div>
              
              {ads.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {ads.map((ad: any) => (
                    <Link key={ad.id} to={`/ads/${ad.slug || ad.id}`} className="block">
                      <div className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 border">
                        <div className="relative aspect-w-16 aspect-h-12 bg-gray-100">
                          {ad.primaryImageUrl ? (
                            <img 
                              src={ad.primaryImageUrl} 
                              alt={ad.title}
                              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                              <PhotoIcon className="h-12 w-12 text-gray-400" />
                            </div>
                          )}
                          
                          {/* Status badges */}
                          <div className="absolute top-3 right-3 flex flex-col gap-2">
                            {ad.isTopAd && (
                              <span className="bg-red-500 text-white px-2 py-1 rounded text-xs font-semibold">
                                ویژه
                              </span>
                            )}
                            {ad.isUrgent && (
                              <span className="bg-orange-500 text-white px-2 py-1 rounded text-xs font-semibold">
                                فوری
                              </span>
                            )}
                          </div>

                          {/* Favorite button */}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleToggleFavorite(ad.id);
                            }}
                            className="absolute top-3 left-3 p-2 bg-white/90 hover:bg-white rounded-full shadow-sm transition-all duration-200"
                          >
                            {ad.isFavorited ? (
                              <HeartIconSolid className="h-5 w-5 text-red-500" />
                            ) : (
                              <HeartIcon className="h-5 w-5 text-gray-600 hover:text-red-500" />
                            )}
                          </button>
                        </div>
                        
                        <div className="p-4">
                          <div className="mb-3">
                            <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-blue-600 transition-colors duration-200">
                              {ad.title}
                            </h3>
                            <p className="text-sm text-gray-600 line-clamp-1">
                              {ad.location || `${business.city || ''} ${business.province || ''}`.trim()}
                            </p>
                          </div>
                          
                          <div className="mb-3">
                            <p className="text-xl font-semibold text-gray-900">
                              ${ad.price.toLocaleString('en-US')}
                            </p>
                          </div>

                          {/* Property details */}
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                            <div className="flex items-center space-x-reverse space-x-3">
                              {ad.bedrooms && (
                                <span className="flex items-center">
                                  🛏️ {ad.bedrooms}
                                </span>
                              )}
                              {ad.bathrooms && (
                                <span className="flex items-center">
                                  🚿 {ad.bathrooms}
                                </span>
                              )}
                              {ad.area && (
                                <span className="flex items-center">
                                  📐 {ad.area}m²
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Stats */}
                          <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100">
                            <div className="flex items-center space-x-reverse space-x-4">
                              <span className="flex items-center">
                                <EyeIcon className="h-3 w-3 ml-1" />
                                {ad.viewCount || 0}
                              </span>
                              <span className="flex items-center">
                                <HeartIcon className="h-3 w-3 ml-1" />
                                {ad.favoriteCount || 0}
                              </span>
                            </div>
                            <span className="text-gray-400">
                              {ad.createdAt ? new Date(ad.createdAt).toLocaleDateString('fa-IR') : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <BuildingOfficeIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">هنوز آگهی‌ای ثبت نشده است</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Business Hours */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ClockIcon className="h-5 w-5 text-gray-500 ml-2" />
                ساعات کاری
              </h3>
              <div className="space-y-3">
                {Object.entries(businessHours).map(([day, hours]) => (
                  <div key={day} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                    <span className="text-gray-700 font-medium">{getDayName(day)}</span>
                    <span className="text-gray-600">
                      {hours.closed ? (
                        <span className="text-red-600 font-medium">بسته</span>
                      ) : (
                        <span className="text-gray-700">
                          {formatTime(hours.open)} - {formatTime(hours.close)}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Location Map */}
            {(business.city || business.province) && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <MapPinIcon className="h-5 w-5 text-gray-500 ml-2" />
                  مکان
                </h3>
                <div className="space-y-4">
                  <div className="text-gray-700 font-medium bg-gray-50 p-3 rounded-md">
                    <p>{business.city}{business.city && business.province && ', '}{business.province}</p>
                  </div>
                  {/* Placeholder for map */}
                  <div className="w-full h-48 bg-gray-100 rounded-md flex items-center justify-center border">
                    <div className="text-center text-gray-500">
                      <MapPinIcon className="h-8 w-8 mx-auto mb-2" />
                      <p className="text-sm font-medium">نقشه مکان</p>
                      <p className="text-xs">(در نسخه آینده فعال خواهد شد)</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="h-5 w-5 text-gray-500 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                آمار سریع
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">کل آگهی‌ها</span>
                  <span className="font-semibold text-gray-900">{ads.length}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">آگهی‌های فعال</span>
                  <span className="font-semibold text-green-600">
                    {ads.filter((ad: any) => ad.status === 'Active').length}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">کل بازدیدها</span>
                  <span className="font-semibold text-gray-900">
                    {ads.reduce((sum: number, ad: any) => sum + (ad.viewCount || 0), 0).toLocaleString('fa-IR')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Modal */}
      {showGalleryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-full overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <PhotoIcon className="h-5 w-5 text-gray-500 ml-2" />
                گالری تصاویر
              </h3>
              <button
                onClick={() => setShowGalleryModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {businessGallery.map((image: string, index: number) => (
                  <div key={index} className="aspect-square bg-gray-100 rounded-md overflow-hidden border">
                    <img 
                      src={image} 
                      alt={`Image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessStorefrontPage; 